import api from './api';

export enum EventCategory {
    MUSIC = 'music',
    NIGHTLIFE = 'night_life',
    THEATRE = 'theatre',
    HOBBIES = 'hobbies',
    BUSINESS = 'business',
    ONLINE = 'online',
    FAB = 'food_and_beverages',
    CHARITY = 'charity',
    FREE = 'free',
    DATING = 'dating',
    HOLIDAYS = 'holidays'
}

// Type matching the event response from the API
export type EventResponse = {
    id: number;
    title: string;
    description: string;
    organizerDescription?: string;
    venue: string;
    startTime: string;
    endTime: string;
    imageUrl: string | null;
    creatorId: number;
    createdAt: string;
    updatedAt: string;
    isActive: boolean;
    isDraft: boolean;
    isVirtual: boolean;
    isFreeOfCharge: boolean;
    sellingPrice: number;
    anchorPrice: number;
    capacity: number;
    virtualEventLink: string | null;
    category?: EventCategory;
    artistDescription?: string;
};

// Type for creating a new event
export type CreateEventData = {
    title: string;
    description: string;
    venue: string;
    startTime: string;
    endTime: string;
    imageUrl: string | null;
    isDraft: string;
    isVirtual: string;
    isFreeOfCharge: string;
    sellingPrice: string;
    anchorPrice: string;
    capacity: string;
    virtualEventLink?: string | null;
    artistDescription?: string;
};

// Type for search response
export type SearchEventsResponse = {
    events: EventResponse[];
    total: number;
    page: number;
    limit: number;
    totalPages: number;
};

// Type for typeahead suggestions
export type TypeaheadSuggestion = {
    title: string;
    id: string;
};

// Type for search parameters
export type SearchEventsParams = {
    query?: string;
    venue?: string;
    startDate?: string;
    endDate?: string;
    isVirtual?: boolean;
    isFreeOfCharge?: boolean;
    minPrice?: number;
    maxPrice?: number;
    page?: number;
    limit?: number;
    category?: EventCategory;
    sortBy?: 'date' | 'price' | 'popularity';
    sortOrder?: 'asc' | 'desc';
};

export const fetchAllEvents = async (): Promise<EventResponse[]> => {
    const response = await api.get<EventResponse[]>('/all-events');
    return response.data;
};

export const getEvents = async () => {
  const response = await api.get('/events');
  return response.data;
};

// Original method that accepts FormData (keeping for backward compatibility)
export const createEventWithFormData = async (formData: FormData) => {
  const response = await api.post('/events', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response.data;
};

// New method that accepts JSON data with S3 image URL
export const createEvent = async (eventData: CreateEventData) => {
  const response = await api.post('/events', eventData, {
    headers: {
      'Content-Type': 'application/json',
    },
  });
  return response.data;
};

export const searchEvents = async (params: SearchEventsParams): Promise<SearchEventsResponse> => {
    // Build query parameters
    const queryParams = new URLSearchParams();
    
    if (params.query) queryParams.append('q', params.query);
    if (params.venue) queryParams.append('venue', params.venue);
    if (params.startDate) queryParams.append('startDate', params.startDate);
    if (params.endDate) queryParams.append('endDate', params.endDate);
    if (params.isVirtual !== undefined) queryParams.append('isVirtual', params.isVirtual.toString());
    if (params.isFreeOfCharge !== undefined) queryParams.append('isFreeOfCharge', params.isFreeOfCharge.toString());
    if (params.minPrice !== undefined) queryParams.append('minPrice', params.minPrice.toString());
    if (params.maxPrice !== undefined) queryParams.append('maxPrice', params.maxPrice.toString());
    if (params.page) queryParams.append('page', params.page.toString());
    if (params.limit) queryParams.append('limit', params.limit.toString());
    if (params.category) queryParams.append('category', params.category);
    if (params.sortBy) queryParams.append('sortBy', params.sortBy);
    if (params.sortOrder) queryParams.append('sortOrder', params.sortOrder);

    const response = await api.get<EventResponse[]>(`/all-events/search?${queryParams.toString()}`);
    
    // Transform the response to match the expected structure
    return {
        events: response.data,
        total: response.data.length,
        page: params.page || 1,
        limit: params.limit || 10,
        totalPages: Math.ceil(response.data.length / (params.limit || 10))
    };
};

export const getTypeaheadSuggestions = async (query: string): Promise<TypeaheadSuggestion[]> => {
    if (!query) {
        throw new Error('Search query is required');
    }
    const response = await api.get<string[]>(`/all-events/typeahead?q=${encodeURIComponent(query)}`);
    
    // Transform the response to match the expected structure
    return response.data.map((title, index) => ({
        title,
        id: index.toString() // Convert index to string for ID
    }));
};

export const getNearbyEvents = async (lat: number, lng: number): Promise<EventResponse[]> => {
    // TODO: Mohonish - Restore location-based filtering when ready
    // Temporarily returning all events instead of location-based filtering
    const response = await api.get<EventResponse[]>('/all-events');
    return response.data;
    
    // Original location-based filtering code (commented out for now):
    // if (!lat || !lng) {
    //     throw new Error('Latitude and Longitude are required');
    // }
    // const response = await api.get<EventResponse[]>(`/all-events/nearby?lat=${lat}&lng=${lng}`);
    // return response.data;
};

export const getPopularEvents = async (): Promise<EventResponse[]> => {
    // TODO: Mohonish - Restore popular events filtering when ready
    // Temporarily returning all events instead of popular filtering
    const response = await api.get<EventResponse[]>('/all-events');
    return response.data;
};

export const getRecommendedEvents = async (): Promise<EventResponse[]> => {
    // TODO: Mohonish - Restore recommended events filtering when ready
    // Temporarily returning all events instead of recommendation filtering
    const response = await api.get<EventResponse[]>('/all-events');
    return response.data;
};

export const getUpcomingEvents = async (): Promise<EventResponse[]> => {
    // TODO: Mohonish - Restore upcoming events filtering when ready
    // Temporarily returning all events instead of upcoming filtering
    const response = await api.get<EventResponse[]>('/all-events');
    return response.data;
};

export const getEventById = async (eventId: string): Promise<EventResponse> => {
    const response = await api.get<EventResponse>(`/all-events/${eventId}`);
    return response.data;
};

export const updateEvent = async (eventId: string, eventData: CreateEventData): Promise<EventResponse> => {
  const response = await api.put<EventResponse>(`/events/${eventId}`, eventData);
  return response.data;
};